import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";


const SelectLinkedInPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("sessionId");
  const userName = localStorage.getItem("UserName");
  const navigate = useNavigate();

  const [selectedOrg, setSelectedOrg] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (sessionId) {
      fetchLinkedInData();
    } else {
      setError("No session ID provided");
      setLoading(false);
    }
  }, [sessionId]);

  const fetchLinkedInData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/page-selection-data/${sessionId}`
      );
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching LinkedIn data:", err);
      setError(err.response?.data?.error || "Failed to load page data");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedOrg) return alert("Please select a page");
    if (!data) return alert("No data available");

    try {
      // Use the new API endpoint to save the selected page
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/save-selected-page`,
        {
          sessionId,
          selectedOrgId: selectedOrg
        }
      );

      if (response.data.success) {
        const { tokenID, isOnboarding } = response.data;
        
        // If this is an onboarding flow, redirect to onboarding callback
        if (isOnboarding) {
          // Redirect to onboarding callback with success
          window.location.href = `${window.location.origin}/onboarding-linkedin-callback?tokenID=${tokenID}&success=true`;
          return;
        }
        
        // Regular flow - save to localStorage and navigate to Pagehome
        // Find the selected organization profile for local storage
        const orgProfile = data.orgProfiles.find((p) => p.id === selectedOrg);
        const orgProfileWithLogo = data.orgProfilesWithLogo.find(
          (p) => p.responseData.id == selectedOrg
        );

        const profileUrl = orgProfileWithLogo?.logoIdentifier || "";

        const pageProfileData = [
          {
            UserID: data.UserID,
            FullName: orgProfile.localizedName,
            OrganizationName: orgProfile.localizedName,
            ProfileUrl: profileUrl,
            AccessToken: "", // Not needed in localStorage since it's handled server-side
            Tokenid: orgProfile.id,
            AccountType: "Page",
            PlatformName: "Linkedin",
            IsActive: true,
          },
        ];

        localStorage.setItem("userProfile", JSON.stringify(pageProfileData));
        localStorage.setItem("AccessToken", ""); // Will be fetched from server when needed
        localStorage.setItem("Tokenid", orgProfile.id);
        localStorage.setItem("FullName", orgProfile.localizedName);
        localStorage.setItem("OrganizationName", orgProfile.localizedName);
        localStorage.setItem("ProfileUrl", profileUrl);
        localStorage.setItem("AccountType", "Page");

        navigate("/Pagehome");
      } else {
        alert("Failed to save selected page. Please try again.");
      }
    } catch (err) {
      console.error("Error saving selected page:", err);
      alert("Error saving selected page. Please try again.");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f4f6fb",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "18px", color: "#2e3192", marginBottom: "20px" }}>
            Loading LinkedIn pages...
          </div>
          <div style={{ fontSize: "14px", color: "#666" }}>
            Please wait while we fetch your available pages.
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f4f6fb",
        }}
      >
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "18px", color: "#e74c3c", marginBottom: "20px" }}>
            Error Loading Pages
          </div>
          <div style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
            {error}
          </div>
          <button
            onClick={() => window.location.href = "/Pagehome"}
            style={{
              padding: "10px 20px",
              backgroundColor: "#2e3192",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!data || !data.orgProfiles || data.orgProfiles.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f4f6fb",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "18px", color: "#2e3192", marginBottom: "20px" }}>
            No LinkedIn Pages Found
          </div>
          <div style={{ fontSize: "14px", color: "#666" }}>
            No LinkedIn pages were found for your account.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "40px 0",   // ✅ adds spacing without forcing extra height
        backgroundColor: "#f4f6fb",
        minHeight: "100vh",  // optional: ensures it at least fills screen height
        alignItems: "flex-start" // ✅ content starts at top, not vertically centered
      }}
    >
      <div
        style={{
          padding: "40px",
          borderRadius: "20px",
          backgroundColor: "#fff",
          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
          width: "700px",
        }}
      >
        <h3
          style={{
            marginBottom: "30px",
            color: "#2e3192",
            textAlign: "center",
            fontSize: "22px",
          }}
        >
          Select a LinkedIn Page
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "20px",
            marginBottom: "30px",
          }}
        >
          {data.orgProfiles.map((org) => {
            const isSelected = selectedOrg === org.id;
            const orgWithLogo = data.orgProfilesWithLogo.find(
              (p) => p.responseData.id == org.id
            );
            const logoUrl =
              orgWithLogo?.logoIdentifier || "https://via.placeholder.com/80";

            return (
              <div
                key={org.id}
                onClick={() => setSelectedOrg(org.id)}
                style={{
                  position: "relative",
                  padding: "20px",
                  borderRadius: "15px",
                  border: isSelected ? "2px solid #2e3192" : "1px solid #ddd",
                  backgroundColor: isSelected ? "#eef2ff" : "#fff",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: isSelected
                    ? "0 8px 20px rgba(46,49,146,0.25)"
                    : "0 4px 12px rgba(0,0,0,0.08)",
                  textAlign: "center",
                  transform: isSelected ? "scale(1.05)" : "scale(1)",
                }}
              >
                {/* Tick mark overlay when selected */}
                {isSelected && (
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      backgroundColor: "#2e3192",
                      color: "#fff",
                      borderRadius: "50%",
                      width: "22px",
                      height: "22px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    ✓
                  </div>
                )}

                {/* Profile Picture */}
                <img
                  src={logoUrl}
                  alt={org.localizedName}
                  style={{
                    width: "70px",
                    height: "70px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    marginBottom: "12px",
                    border: isSelected ? "2px solid #2e3192" : "1px solid #ccc",
                  }}
                />

                {/* Name */}
                <div
                  style={{
                    fontWeight: "600",
                    color: isSelected ? "#2e3192" : "#333",
                    fontSize: "15px",
                  }}
                >
                  {org.localizedName || `Page ${org.id}`}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: "center" }}>
          <button
            onClick={() => handleConfirm(selectedOrg)}
            disabled={!selectedOrg}
            style={{
              padding: "14px 36px",
              backgroundColor: selectedOrg ? "#2e3192" : "#ccc",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              cursor: selectedOrg ? "pointer" : "not-allowed",
              fontWeight: "bold",
              fontSize: "16px",
              boxShadow: selectedOrg
                ? "0 6px 16px rgba(46,49,146,0.3)"
                : "none",
              transition: "all 0.3s ease",
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectLinkedInPage;
